import { useAppTheme } from '@/shared/theme';
import { SxProps, Theme } from '@mui/material';
import { Diff } from '@/shared/ui/data/EditableGrid/types/core.type';

export function useGridTheme() {
  const { theme } = useAppTheme();
  const tokens = theme.grid;

  const getCellSx = (options: {
    isHeader?: boolean;
    focused?: boolean;
    selected?: boolean;
    editing?: boolean;
    disabled?: boolean;
    diff?: Diff;
  }): SxProps<Theme> => {
    const { isHeader, focused, selected, editing, disabled, diff } = options;

    let bgcolor = tokens.cell.background.default;
    if (isHeader) {
      bgcolor = tokens.cell.background.header;
    } else if (selected) {
      bgcolor = tokens.cell.background.selected;
    } else if (diff === 'added') {
      bgcolor = tokens.diff.added.background;
    } else if (diff === 'changed') {
      bgcolor = tokens.diff.changed.background;
    } else if (diff === 'deleted') {
      bgcolor = tokens.diff.deleted.background;
    }

    return {
      p: tokens.toolbar.padding,
      border: 1,
      borderColor: tokens.cell.border.default,
      minHeight: tokens.cell.minHeight,
      height: tokens.cell.minHeight,
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      bgcolor,
      cursor: editing ? 'text' : disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? tokens.cell.disabledOpacity : 1,
      fontWeight: isHeader ? 'bold' : 'normal',
      textDecoration: diff === 'deleted' ? tokens.diff.deleted.textDecoration : 'none',
      color: focused
        ? tokens.cell.text.focused
        : isHeader
          ? tokens.cell.text.header
          : tokens.cell.text.default,
      outline: 'none',
      '&:focus': {
        outline: 'none',
      },
      '&:hover':
        !disabled && !editing
          ? {
              bgcolor: selected ? tokens.cell.background.selected : tokens.cell.background.hover,
            }
          : undefined,
      ...(focused && {
        boxShadow: tokens.cell.focusRing,
      }),
    };
  };

  const getContainerSx = (
    gridTemplateColumns: string,
    allowCopySelect?: boolean
  ): SxProps<Theme> => ({
    display: 'grid',
    gridTemplateColumns,
    border: 1,
    borderColor: tokens.container.border,
    borderRadius: tokens.container.borderRadius,
    bgcolor: tokens.container.background,
    overflow: 'auto',
    userSelect: allowCopySelect ? 'text' : 'none',
    width: '100%',
  });

  const toolbarSx: SxProps<Theme> = {
    mb: 1,
    display: 'flex',
    gap: tokens.toolbar.gap,
    p: tokens.toolbar.padding,
    bgcolor: tokens.toolbar.background,
    borderRadius: tokens.toolbar.borderRadius,
    border: 1,
    borderColor: tokens.toolbar.border,
    width: '100%',
  };

  return {
    tokens,
    getCellSx,
    getContainerSx,
    toolbarSx,
  };
}
